<template>
    <b-col sm="12" md="12">
      <b-row>
        <b-col sm="12" :lg="config.fieldSize" md="4" v-for="field in fields" v-bind:key="field.name">
          <b-card no-body>
            <div slot="header">
              {{ field.key }} <span v-if="field.constraints.required">*</span>
               <i class="icon-link" v-if="field.relation.foreign != ''" v-b-tooltip.hover :title="field.relation.type"></i>
               <i class="icon-ban" v-if="!field.visible" v-b-tooltip.hover title="Field is not visible on Create/Edit"></i>
              <div class="card-header-actions">
                <b-link href="#" class="card-header-action btn-setting">
                  <i class="cui-paperclip" @click="cloneField(field)"></i>
                </b-link>
                <b-link href="#" class="card-header-action btn-setting" v-if="!config.dependencyProtected">
                  <i class="icon-settings" @click="editModal(field)" ></i>
                </b-link>
                <b-link href="#" class="card-header-action btn-setting" v-if="!config.dependencyProtected">
                  <i class="icon-trash" @click="remove(field)" ></i>
                </b-link>
              </div>
            </div>
            <b-card-body>
                <p><b>{{ $t('key') }}</b>: <b-badge variant="dark">{{ field.key }}</b-badge></p>
                <p><b>{{ $t('module') }}</b>: <b-badge variant="dark">{{ field.module }}</b-badge></p>
                <p><b>{{ $t('type') }}</b>: <b-badge variant="dark">{{ field.type }}</b-badge></p>
                <p><b>{{ $t('permissions') }}</b>: <b-badge variant="dark" v-for="perm in formatFieldPermissions(field)" :key="perm">{{ perm }}</b-badge></p>
            </b-card-body>
          </b-card>
         </b-col>
        </b-row>
        <nav>
          <div class="form-actions float-right" style="margin: 10px">
            <helptip helpType="icon" tipId="component.settings.fields">
              <b-button variant="success" @click="newFieldModalShow()">{{ $t('addNewField') }}</b-button>
            </helptip>
          </div>
        </nav>

        <main-dialog :title="editFieldModal.field.name" v-model="editFieldModal.show" @hide="hideEditField" @ok="editField" ok-title="Save" ok-variant="success">
          <b-row>
            <b-col sm="12">
              <InputField name="name" :label="$t('name')" v-model="editFieldModal.field.name" @error="fieldError"
                      :rules="{required:true, length: {min: 2}}"
                      :validated="true"
                  />

              <b-form-group
                    :label="this.$t('label')"
                    label-for="label"
                    :label-cols="12"
                    :horizontal="false">
                    <Texti18n taggable v-model="editFieldModal.field.label" />
                </b-form-group>

              <InputField name="key" :label="this.$t('key')" v-model="editFieldModal.field.key" @error="fieldError"
                  :rules="{required:true, length: {min: 2}}"
                  :disabled="true"
              />

              <template v-if="!config.custom">
                <b-form-group
                  :label="this.$t('description')"
                  label-for="description"
                  :label-cols="12"
                  :horizontal="false">
                  <Texti18n taggable v-model="editFieldModal.field.description" />
                </b-form-group>

                <InputField type="number" name="sort_order" label="Sort order" v-model.number="editFieldModal.field.sortOrder" @error="fieldError"
                    :rules="{}"
                />

                <b-form-group v-if="!this.config.hideGroup"
                    :label="this.$t('group')"
                    :description="this.$t('splitFields')"
                    label-for="group"
                    :label-cols="2"
                    :horizontal="false">
                    <v-select taggable v-model="editFieldModal.field.group" :options="fieldGroups"/>
                </b-form-group>

                <b-form-group v-if="!this.config.hideProtected"
                    :label="this.$t('protected')"
                    :description="this.$t('protectedField')"
                    label-for="protected"
                    :label-cols="2"
                    :horizontal="false">
                  <c-switch class="mx-1" color="success" variant="pill" v-model="editFieldModal.field.protected"/>
                </b-form-group>

                <b-form-group v-if="!this.config.hideVisible"
                    :label="this.$t('visible')"
                    :description="this.$t('fieldNotVisible')"
                    label-for="visible"
                    :label-cols="2"
                    :horizontal="false">
                  <c-switch class="mx-1" color="success" variant="pill" v-model="editFieldModal.field.visible"/>
                </b-form-group>
                <b-form-group
                    label-for="permissions"
                    :label-cols="3"
                    :horizontal="false">

                    <template #label>
                      {{ $t('permissions') }}

                      <protokol-tooltip id="permissionsTooltip" variant="warning"> 
                          <template #icon>!</template>
                          <template #content>
                              {{ $t('fieldPermissionsDescription') }}
                          </template>
                      </protokol-tooltip>
                    </template>

                    <v-select :options="availablePermissions" v-model="editFieldModal.field.permissions" multiple taggable/>
                </b-form-group>
                <b-form-group
                    :label="this.$t('roles')"
                    label-for="roles"
                    :label-cols="12"
                    :horizontal="false">
                    <b-form-group
                        :label="this.$t('see')"
                        label-for="roles-see"
                        :label-cols="2"
                        :horizontal="false">
                          <v-select :options="roles" v-model="editFieldModal.field.roles.see" :reduce="(o) => o.value" multiple/>
                    </b-form-group>
                    <b-form-group
                        :label="this.$t('edit')"
                        label-for="roles-edit"
                        :label-cols="2"
                        :horizontal="false">
                          <v-select :options="roles" v-model="editFieldModal.field.roles.edit" :reduce="(o) => o.value" multiple/>
                    </b-form-group>
                </b-form-group>
              </template>

            </b-col>
          </b-row>

          <b-row v-if="!config.custom">
            <b-col sm="12">
              <b-row sm="6">
                <b-col sm="12">
                  <SelectField name="module" :label="this.$t('module')" v-model="editFieldModal.field.module" @error="fieldError"
                      :items="Object.keys(fieldDefs.modules)"
                      :reduce="(o) => o"
                      :rules="{required: true, protected: true}"
                  />

                  <SelectField name="type" :label="this.$t('type')" v-model="editFieldModal.field.type" @error="fieldError"
                      :items="fieldDefs.modules[editFieldModal.field.module]"
                      :reduce="(o) => o"
                      :rules="{required: true, protected: true}"
                      :helpTip="helpfulTip(editFieldModal.field)"
                  />

                 <b-form-group
                  :label="this.$t('context')"
                  label-for="context"
                  :label-cols="3"
                  :horizontal="false">
                  <ContextEditor v-model="editFieldModal.field.context" :field="editFieldModal.field" :fields="fields"/>
                </b-form-group>
                 <b-form-group
                  :label="this.$t('constraints')"
                  label-for="constraints"
                  :label-cols="3"
                  :horizontal="false">
                  <ConstraintsEditor v-model="editFieldModal.field.constraints" :field="editFieldModal.field"/>
                </b-form-group>
                <b-form-group
                  v-if="canDisplayRelationships(editFieldModal.field)"
                  :label="this.$t('relationships')"
                  label-for="relationships"
                  :label-cols="3"
                  :horizontal="false">
                  <RelationshipsEditor v-model="editFieldModal.field.relation" :fields="fields" :field="editFieldModal.field"/>
                </b-form-group>
                <b-form-group
                  v-if="canDisplayTemplate(editFieldModal.field)"
                  :label="this.$t('template')"
                  label-for="template"
                  :label-cols="3"
                  :horizontal="false">
                  <Template v-model="editFieldModal.field.template" />
                </b-form-group>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

        </main-dialog>

        <main-dialog title="New field" v-model="newFieldModal.show" @ok="addNewField">
          <b-row>
            <b-col sm="12">
              <InputField name="name" :label="this.$t('name')" v-model="newFieldModal.field.name" @error="fieldError"
                  :rules="{required:true, length: {min: 2}}"
              />

              <b-form-group
                :label="this.$t('label')"
                label-for="label"
                :label-cols="12"
                :horizontal="false">
                <Texti18n taggable v-model="newFieldModal.field.label" />
              </b-form-group>

              <InputField name="key" :label="this.$t('key')" v-model="newFieldModal.field.key" @error="fieldError"
                  :rules="{required:true, length: {min: 2}}"
                  :disabled="autogenerateKey"
              >
                 <b-form-group
                  label=""
                  :description="this.$t('autogenerateChecked')"
                  label-for="basicCheckboxes"
                  :label-cols="0"
                  horizontal>
                    <b-form-checkbox v-model="autogenerateKey">{{ $t('autogenerate') }}</b-form-checkbox>
                </b-form-group>
              </InputField>

              <template v-if="!config.custom">
                <b-form-group
                  :label="this.$t('description')"
                  label-for="description"
                  :label-cols="12"
                  :horizontal="false">
                  <Texti18n taggable v-model="newFieldModal.field.description" />
                </b-form-group>

                <InputField type="number" name="sort_order" :label="this.$t('sortOrder')" v-model.number="newFieldModal.field.sortOrder" @error="fieldError"
                    :rules="{}"
                    :helpTip="{id: 'event.operation.mustache', text: this.$t('usePlaceholders')}"
                    autofill="off"
                />

                <b-form-group v-if="!this.config.hideGroup"
                    :label="this.$t('group')"
                    :description="this.$t('splitFields')"
                    label-for="group"
                    :label-cols="2"
                    :horizontal="false">
                    <v-select taggable v-model="newFieldModal.field.group" :options="fieldGroups"/>
                </b-form-group>

                <b-form-group v-if="!this.config.hideProtected"
                    :label="this.$t('protected')"
                    :description="this.$t('protectedField')"
                    label-for="protected"
                    :label-cols="2"
                    :horizontal="false">
                  <c-switch class="mx-1" color="success" variant="pill" v-model="newFieldModal.field.protected"/>
                </b-form-group>

                <b-form-group v-if="!this.config.hideVisible"
                    :label="this.$t('visible')"
                    :description="this.$t('fieldNotVisible')"
                    label-for="visible"
                    :label-cols="2"
                    :horizontal="false">
                  <c-switch class="mx-1" color="success" variant="pill" v-model="newFieldModal.field.visible"/>
                </b-form-group>
                <b-form-group
                    :label="this.$t('permissions')"
                    label-for="permissions"
                    :label-cols="2"
                    :horizontal="false">
                    <Helptip helpType="icon-tooltip" :tip="$t('fieldPermissionsDescription')">
                      <v-select :options="availablePermissions" v-model="newFieldModal.field.permissions" multiple taggable/>
                  </Helptip>
                </b-form-group>
              </template>
            </b-col>
          </b-row>

          <b-row v-if="!config.custom">
            <b-col sm="12">
              <b-row sm="6">
                <b-col sm="12">
                  <SelectField name="module" :label="this.$t('module')" v-model="newFieldModal.field.module" @error="fieldError"
                      :items="Object.keys(fieldDefs.modules)"
                      :reduce="(o) => o"
                      :rules="{required: true}"
                  />

                  <SelectField name="type" :label="this.$t('type')" v-model="newFieldModal.field.type" @error="fieldError"
                      :items="fieldDefs.modules[newFieldModal.field.module]"
                      :reduce="(o) => o"
                      :rules="{required: true}"
                      :helpTip="helpfulTip(newFieldModal.field)"
                  />

                <b-form-group
                  :label="this.$t('Context')"
                  label-for="ccntext"
                  :label-cols="2"
                  :horizontal="false">
                  <ContextEditor v-model="newFieldModal.field.context" :field="newFieldModal.field" :fields="fields"/>
                </b-form-group>
                 <b-form-group
                  :label="this.$t('constraints')"
                  label-for="constraints"
                  :label-cols="2"
                  :horizontal="false">
                  <ConstraintsEditor v-model="newFieldModal.field.constraints" :field="newFieldModal.field"/>
                </b-form-group> 
                <b-form-group
                  v-if="canDisplayRelationships(newFieldModal.field)"
                  :label="this.$t('relationships')"
                  label-for="relationships"
                  :label-cols="3"
                  :horizontal="false">
                  <RelationshipsEditor v-model="editFieldModal.field.relation" :fields="fields" :field="newFieldModal.field"/>
                </b-form-group>
                <b-form-group
                   v-if="canDisplayTemplate(newFieldModal.field)"
                   :label="this.$t('template')"
                  label-for="template"
                  :label-cols="2"
                  :horizontal="false">
                  <Template v-model="newFieldModal.field.template" />
                </b-form-group>
                 
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <template v-slot:modal-footer>
            <div class="w-100">
              <b-button
                variant="success"
                class="float-right m-2"
                @click="addNewField"
              >
                {{ $t('saveContinue') }}
              </b-button>
              <b-button
                variant="primary"
                class="float-right m-2"
                @click="addNewFieldAndClose"
              >
                {{ $t('saveClose') }}
              </b-button>
            </div>
          </template>
        </main-dialog>
      </b-col>
</template>

<script>

import fieldDefs from "@/components/identityFields/defs"
import fieldObj from "@/components/identityFields/field"
import Mutators from "./Mutators"
import Relationships from "./Relationships"
import Template from "./Template"
import Constraints from "./Constraints"
import Context from "./Context"
import { Switch as cSwitch } from '@coreui/vue'
import Input from "@/components/form/Input"
import Select from "@/components/form/Select"
import _ from "lodash"
import Helptip from '../../elements/Helptip.vue'
import VSelect from 'vue-select'

// css
import Texti18n from "../../form/Texti18n.vue"

export default {
  components: {
    MutatorsEditor: Mutators,
    RelationshipsEditor: Relationships,
    ConstraintsEditor: Constraints,
    ContextEditor: Context,
    cSwitch,
    InputField: Input,
    SelectField: Select,
    Helptip,
    VSelect,
    Texti18n,
    Template
},
  props: {
      value: {
        type: Array,
        default: function() {
          return []
        }
      },
      config: {
        type: Object,
        default: function() {
          return {
            fieldDefs: fieldDefs,
            hideGroup: false,
            hideProtected: false,
            hideVisible: false,
            fieldSize: 4,
            custom: false,
            dependencyProtected: false
          }
        }
      },
  },
  data: function() {
    return {
      autogenerateKey: true,
      errors: [],
      newFieldModal: {
        show: false,
        field: _.cloneDeep(fieldObj)
      },

      editFieldModal: {
        show: false,
        field: _.cloneDeep(fieldObj)
      }
    }
  },
  watch: {
    "newFieldModal.field.name": function(val) {
      if (this.autogenerateKey) {
         this.$set(this.newFieldModal.field, 'key',  _.replace(_.toLower(val), /\s/g, "_"))
      }

      this.newFieldModal.field.name = _.startCase(_.toLower(val))
    },

    "editFieldModal.field.name": function(val) {
      this.editFieldModal.field.name = _.startCase(_.toLower(val))
    },

    "newFieldModal.field.key": function(val) {
      if (!this.autogenerateKey) {
        this.$set(this.newFieldModal.field, 'key',  _.trim(_.replace(_.replace(_.toLower(val), " ", "_"), "__", "_"), "_"))
      }
    },

    autogenerateKey: function(val) {
      if (val) {
        this.newFieldModal.field.key = _.replace(_.toLower(this.newFieldModal.field.name), /\s/g, "_")
      }
    }
  },
  computed: {
    availablePermissions() {
      let perms = ["public-app","platform-app","public-api"]

      let publicApps = this.$store.getters.getPublicApps || []
      publicApps.forEach(p => {
        perms.push(`public-app::${p.name}`)
      })

      let platformApps = this.$store.getters.getApps || []
      platformApps.forEach(p => {
        perms.push(`platform-app::${p.name}`)
      })

      return perms
    },
    fields() {
      let fields = this.value || []

      fields.sort((a, b) => {
        return a.sortOrder - b.sortOrder
      })

      return fields
    },

    fieldGroups() {
      let groups = {}

      this.fields.forEach(f => {
        if (f.group !== undefined) {
          groups[f.group] = true
        }
      })

      return Object.keys(groups);
    },

    fieldDefs() {
      if (this.config !== null) {
        if (this.config.fieldDefs) {
          return this.config.fieldDefs
        }
      }

      return fieldDefs
    },

    roles() {
      return this.$store.getters.getRoles.map(r => {
        return {
          label: r.name,
          value: r.uuid
        }
      })
    }
  },
  methods: {
    editModal(field) {
      field = _.cloneDeep(field)
      let fieldO = _.cloneDeep(fieldObj) 

      this.editFieldModal.field = {
        ...fieldO,
        ...field
      }

      this.editFieldModal.show = true
      this.editFieldModal.fieldOld = field
    },

     cloneField(field) {
       // reset keys so new name and key can be set
      let cField =  _.cloneDeep(field)
      cField.name = ""
      cField.key = ""

      this.newFieldModal.field = cField
      this.newFieldModal.show = true
      this.newFieldModal.fieldOld = cField
    },

    editField(modal) {
      let field = this.editFieldModal.field
      let fields = this.fields.map(el => {
        if (el.key == field.key) {
          return field
        } else {
          return el
        }
      });

      if (this.checkForErrors()) {
        this.$mainDialog.msgBoxOk(this.$t('fieldsNotValid'))

        modal.preventDefault();

        return false
      }

      this.$emit('input', fields)
    },

    addNewField(modal) {
      if (this.checkForErrors()) {
        this.$mainDialog.msgBoxOk(this.$t('fieldsNotValid'))

        modal.preventDefault();

        return false
      }

      if (this.validateFields()) {
        this.$mainDialog.msgBoxOk(this.$t('fieldsAlreadyExist'))

        modal.preventDefault();

        return false
      }

      //console.log(fieldObj)
      this.fields.push(this.newFieldModal.field)
      this.newFieldModal.field = _.cloneDeep(fieldObj)

      this.$emit('input', this.fields)

      return true
    },

    addNewFieldAndClose(modal) {
      if (this.addNewField(modal)) {
        this.newFieldModal.show = false
      }
    },

    hideEditField(modal) {
      if (modal.trigger != 'cancel' && modal.trigger != 'ok') {

        if (JSON.stringify(this.editFieldModal.fieldOld) != JSON.stringify(this.editFieldModal.field)) {
          modal.preventDefault()

          this.$mainDialog.msgBoxOk(this.$t('unsavedChanges'));
        }
      }

      if (modal.trigger == 'cancel') {
        this.editFieldModal.field = this.editFieldModal.fieldOld
      }
    },

    remove(field) {
      this.$mainDialog.msgBoxConfirm('Are you sure?').then((value) => {
        if (value) {
          let fields = this.fields.filter(f => {
            return f.key != field.key
          })

          this.$emit('input', fields)
        }
      })
    },

    newFieldModalShow() {
      this.newFieldModal.field = _.cloneDeep(fieldObj)
      this.newFieldModal.show = true
    },

    fieldError(error) {
      let found = this.errors.find((i, e) => {
          return i.field == error.field
      })

      if (found === undefined) {
          this.errors.push(error)
      } else {
          this.errors.map(e => {
              if (found.field == e.field)
                  e.error = error.error
          })
      }
    },

    checkForErrors() {
      // for some fcking weird reason there is no error when Name is set to empty string, it doesn't show that fields is required
      // even though Key field is same and functions properly, fck this stupid shit, this is workaround i dont have time in my life to fck with this nonsense
      if (this.newFieldModal.field.name == "" && this.editFieldModal.field.name == "") {
        return true
      }

      if (this.newFieldModal.field.type == "" && this.editFieldModal.field.type == "") {
        return true
      }

      return this.errors.find((i, e) => {
        return i.error
      })
    },

    validateFields() {
      return this.fields.find(f => {
        return this.newFieldModal.field.key == f.key || this.newFieldModal.field.name == f.name
      })
    },

    helpfulTip(field) {
      if (field.module == "table") {
        if (field.type == "custom") {
           return {id: "identityField.table.custom", type: "icon", title: "Did you know?", size: "lg"}
        }
      }
    },

    canDisplayRelationships(field) {
      let mod = field.module
      let type = field.type

      switch(mod) {
        case 'select': return type == 'relation';
        case 'table': return type == 'default';
        default: return false
      }
    },

    canDisplayTemplate(field) {
      let mod = field.module
      let type = field.type

      switch(mod) {
        default: return true
      }
    },

    formatFieldPermissions(field) {
      if (field.permissions) {
        return field.permissions
      }

      return '*'
    }
  },

  mounted() {
    this.$store.dispatch('fetchRoles')

    console.log(this.editFieldModal.field)
  }
}
</script>
